import React, { useState, useEffect } from 'react';
import LoginComponent from './components/LoginComponent';
import MainMenuComponent from './components/MainMenuComponent';
import InvoiceReportComponent from './components/InvoiceReportComponent';
import SalesOverviewComponent from './components/SalesOverviewComponent';
import CustomerSummaryComponent from './components/CustomerSummaryComponent';
import TopProductsComponent from './components/TopProductsComponent';
import ProductDetailsComponent from './components/ProductDetailsComponent';
import PromotionManagementComponent from './components/PromotionManagementComponent';
import CSReportComponent from './components/CSReportComponent';
import BookingProductComponent from './components/BookingProductComponent';
import MonitorDashboardComponent from './components/MonitorDashboardComponent';
import PresentationDashboardComponent from './components/PresentationDashboardComponent';

function App() {
  // Navigation state (following current pattern)
  const [currentPage, setCurrentPage] = useState('login');
  
  // Authentication state
  const [userData, setUserData] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Session management (following current 7-day pattern)
  const SESSION_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days
  
  // Session validation function
  const checkSessionValidity = () => {
    const loginTime = localStorage.getItem('loginTime');
    const storedLoginStatus = localStorage.getItem('isLoggedIn');
    
    if (storedLoginStatus === 'true' && loginTime) {
      const sessionAge = Date.now() - parseInt(loginTime);
      
      if (sessionAge > SESSION_DURATION) {
        handleLogout();
        return false;
      } else {
        localStorage.setItem('loginTime', Date.now().toString());
        return true;
      }
    }
    return false;
  };

  // Authentication handlers
  const handleLogin = (userInfo) => {
    localStorage.setItem('loginTime', Date.now().toString());
    localStorage.setItem('userData', JSON.stringify(userInfo));
    localStorage.setItem('isLoggedIn', 'true');
    
    setUserData(userInfo);
    setIsLoggedIn(true);
    
    // Check if user was trying to access hidden route before login
    const currentPath = window.location.pathname;
    if (currentPath === '/create_promotion') {
      setCurrentPage('promotion-management');
    } else {
      setCurrentPage('main-menu');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userData');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('loginTime');
    
    setUserData(null);
    setIsLoggedIn(false);
    setCurrentPage('login');
  };


  // Check login status and handle URL routes on mount
  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    const storedLoginStatus = localStorage.getItem('isLoggedIn');
    
    // Check for specific routes
    const currentPath = window.location.pathname;
    
    // Handle presentation route (no login required)
    if (currentPath === '/presentation') {
      setCurrentPage('presentation');
      return;
    }

    // Handle monitor dashboard route (no login required)
    if (currentPath === '/monitor') {
      setCurrentPage('monitor');
      return;
    }

    // Handle CS report route (no login required)
    if (currentPath === '/cs_report') {
      setCurrentPage('cs-report');
      return;
    }

    // Handle booking product route (has its own login)
    if (currentPath === '/booking_product') {
      setCurrentPage('booking-product');
      return;
    }
    
    if (storedLoginStatus === 'true' && storedUserData && checkSessionValidity()) {
      try {
        const parsedUserData = JSON.parse(storedUserData);
        setUserData(parsedUserData);
        setIsLoggedIn(true);
        
        // Handle hidden routes that require login
        if (currentPath === '/create_promotion') {
          setCurrentPage('promotion-management');
        } else {
          setCurrentPage('main-menu');
        }
      } catch (err) {
        console.error('Error parsing stored user data:', err);
        handleLogout();
      }
    } else if (currentPath === '/create_promotion') {
      // If trying to access hidden route without login, redirect to login but remember the route
      setCurrentPage('login');
    }
  }, []);

  // Render logic
  const renderCurrentPage = () => {
    // Handle presentation page (no login required)
    if (currentPage === 'presentation') {
      return <PresentationDashboardComponent />;
    }

    // Handle monitor dashboard (no login required)
    if (currentPage === 'monitor') {
      return <MonitorDashboardComponent />;
    }

    // Handle CS report page (no login required)
    if (currentPage === 'cs-report') {
      return <CSReportComponent />;
    }

    // Handle booking product page (has its own login)
    if (currentPage === 'booking-product') {
      return <BookingProductComponent />;
    }

    if (!isLoggedIn || currentPage === 'login') {
      return <LoginComponent onLogin={handleLogin} />;
    }

    // Handle hidden promotion management page
    if (currentPage === 'promotion-management') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
          <PromotionManagementComponent 
            userData={userData}
            onLogout={handleLogout}
            onBackToMenu={() => setCurrentPage('main-menu')}
          />
        </div>
      );
    }

    return (
      <MainMenuComponent 
        userData={userData}
        onLogout={handleLogout}
      />
    );
  };

  return (
    <div className="App">
      {renderCurrentPage()}
    </div>
  );
}

export default App;